import { LocalStorageKeys, setLocalStorageKey } from '@/utils/local-storage';
import { defineStore } from 'pinia';

export interface ReleaseInfo {
  /** The date of the release (e.g., 2026-01-19) */
  date: string;
  /** The description/summary from frontmatter */
  description: string;
  /** The raw markdown content (without frontmatter) */
  markdownContent: string;
}

/** List of available release note dates */
export interface ReleaseNotesIndex {
  releaseNotes: string[];
}

export enum DevStrideModuleName {
  MAP = 'map',
  ITEMS = 'items',
  ANALYZE = 'analyze',
  AUTOMATE = 'automate',
  NOTIFICATIONS = 'notifications',
  CYCLES = 'cycles',
  BOARDS = 'boards',
  GANTT = 'gantt',
  ACCOUNT = 'account',
  SETTINGS = 'settings',
  WORK = 'work',
  PLUGIN = 'plugin',
}

export interface GeneralStateInterface {
  activeModule: DevStrideModuleName;
  maintenanceMode: boolean;
  toolbarCollapsed: boolean;
  /** The latest release info fetched from docs site */
  latestRelease: ReleaseInfo | null;
  /** true if release notes fetch failed (prevents repeated failed fetches) */
  releaseNotesFetchFailed: boolean;
  /** used for triggering the release dialog from user interaction */
  releaseDialogVisibleFromUserInteraction: boolean;
  /** true when navigating to a new yet-to-be-loaded dynamic route (which is all routes in our app) */
  isNavigating: boolean;
}

export const useGeneralStore = defineStore('general', {
  /****
   STATE
   ****/
  state: (): GeneralStateInterface => ({
    activeModule: DevStrideModuleName.MAP,
    maintenanceMode: false,
    toolbarCollapsed: false,
    latestRelease: null,
    releaseNotesFetchFailed: false,
    releaseDialogVisibleFromUserInteraction: false,
    isNavigating: false,
  }),

  /****
   GETTERS
   ****/
  getters: {},

  /****
   ACTIONS
   ****/
  actions: {
    setActiveModule(activeModule: DevStrideModuleName) {
      this.activeModule = activeModule;
    },
    setMaintenanceMode(maintenanceMode: boolean) {
      this.maintenanceMode = maintenanceMode;
    },
    setToolbarCollapsed(toolbarCollapsed: boolean) {
      this.toolbarCollapsed = toolbarCollapsed;
      setLocalStorageKey(LocalStorageKeys.TOOLBAR_COLLAPSED, toolbarCollapsed);
    },
    async populateReleases() {
      // Don't retry if we already failed (prevents repeated failed fetches)
      if (this.releaseNotesFetchFailed) return;

      try {
        // Fetch the list of available release notes
        const indexResponse = await fetch(
          'https://docs.devstride.com/raw-md/release-notes/index.json'
        );

        if (!indexResponse.ok) {
          throw new Error(`HTTP error: ${indexResponse.status}`);
        }

        const index = (await indexResponse.json()) as ReleaseNotesIndex;

        if (!index.releaseNotes || index.releaseNotes.length === 0) {
          throw new Error('No release notes available');
        }

        // Get the latest release (first in the list, sorted descending)
        const latestDate = index.releaseNotes[0];

        // Fetch the raw markdown for the latest release
        const markdownResponse = await fetch(
          `https://docs.devstride.com/raw-md/release-notes/${latestDate}.md`
        );

        if (!markdownResponse.ok) {
          throw new Error(
            `HTTP error fetching release: ${markdownResponse.status}`
          );
        }

        const rawContent = await markdownResponse.text();

        // Parse frontmatter and content
        const { description, content } = this.parseFrontmatter(rawContent);

        this.latestRelease = {
          date: latestDate,
          description,
          markdownContent: content,
        };
      } catch (err) {
        console.error('Failed to fetch release notes:', err);
        // Set flag to prevent repeated failed fetches during this session
        this.releaseNotesFetchFailed = true;
      }
    },

    /**
     * Parse YAML frontmatter from markdown content
     * Returns the description and content without frontmatter
     */
    parseFrontmatter(raw: string): { description: string; content: string } {
      const frontmatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/;
      const match = raw.match(frontmatterRegex);

      if (!match) {
        return { description: '', content: raw };
      }

      const frontmatter = match[1];
      const content = match[2];

      // Extract description from frontmatter
      const descMatch = frontmatter.match(
        /description:\s*["']?([^"'\n]+)["']?/
      );
      const description = descMatch ? descMatch[1].trim() : '';

      return { description, content };
    },

    setReleaseDialogVisibleFromUserInteraction(
      releaseDialogVisibleFromUserInteraction: boolean
    ) {
      this.releaseDialogVisibleFromUserInteraction =
        releaseDialogVisibleFromUserInteraction;
    },
    setIsNavigating(isNavigating: boolean) {
      this.isNavigating = isNavigating;
    },
  },
});
